import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type OffersListOutput = OutputRouter['offers']['list']
export type OfferItem = OffersListOutput['offers'][number]
export type OffersSearchOutput = OutputRouter['offers']['search']
export type OfferSearchItem = OffersSearchOutput['offers'][number]
export type OfferDetails = OutputRouter['offers']['getById']['offer']
export type CreateOfferOutput = OutputRouter['offers']['create']
export type UpdateOfferOutput = OutputRouter['offers']['update']
export type DeleteOffersOutput = OutputRouter['offers']['delete']
export type BulkCreateOffersOutput = OutputRouter['offers']['bulkCreate']
export type BulkProductSelectionOutput = OutputRouter['offers']['getBulkProductSelection']

// Input types
export type ListOffersInput = InputRouter['offers']['list']
export type SearchOffersInput = InputRouter['offers']['search']
export type GetOfferInput = InputRouter['offers']['getById']
export type CreateOfferInput = InputRouter['offers']['create']
export type UpdateOfferInput = InputRouter['offers']['update']
export type DeleteOffersInput = InputRouter['offers']['delete']
export type UpdateOfferPromotionsInput = InputRouter['offers']['updatePromotions']
export type BulkCreateOffersInput = InputRouter['offers']['bulkCreate']
export type BulkProductSelectionInput = InputRouter['offers']['getBulkProductSelection']

// Query hooks
export const useOffersList = (params: ListOffersInput, options?: {
    enabled?: boolean;
    refetchOnMount?: boolean | 'always';
    refetchOnWindowFocus?: boolean;
}) => {
    const { enabled = true, ...otherOptions } = options || {};
    return trpc.offers.list.useQuery(params, {
        enabled: enabled && !!params.storeId,
        ...otherOptions,
    })
}

export const useOffersSearch = (params: SearchOffersInput, enabled = true) => {
    return trpc.offers.search.useQuery(params, {
        enabled: enabled && !!params.storeId,
    })
}

export const useOffer = (id: string, enabled = true) => {
    return trpc.offers.getById.useQuery(
        { id },
        {
            enabled: enabled && !!id,
            retry: 2,
            staleTime: 30000,
        },
    )
}

export const useBulkProductSelection = (params: BulkProductSelectionInput, enabled = true) => {
    return trpc.offers.getBulkProductSelection.useQuery(params, {
        enabled: enabled && !!params.storeId,
    })
}

// Mutation hooks
export const useCreateOffer = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.offers.create.useMutation({
        onSuccess: () => {
            utils.offers.list.invalidate()
            utils.offers.search.invalidate()
            toast({
                title: 'Success',
                description: 'Offer created successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to create offer',
                variant: 'destructive',
            })
        },
    })
}

export const useUpdateOffer = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.offers.update.useMutation({
        onSuccess: () => {
            utils.offers.list.invalidate()
            utils.offers.search.invalidate()
            utils.offers.getById.invalidate()
            toast({
                title: 'Success',
                description: 'Offer updated successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to update offer',
                variant: 'destructive',
            })
        },
    })
}

export const useDeleteOffers = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.offers.delete.useMutation({
        onSuccess: () => {
            utils.offers.list.invalidate()
            utils.offers.search.invalidate()
            toast({
                title: 'Success',
                description: 'Offer(s) deleted successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to delete offer(s)',
                variant: 'destructive',
            })
        },
    })
}

export const useUpdateOfferPromotions = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.offers.updatePromotions.useMutation({
        onSuccess: () => {
            utils.offers.getById.invalidate()
            utils.offers.list.invalidate()
            toast({
                title: 'Success',
                description: 'Offer promotions updated successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to update offer promotions',
                variant: 'destructive',
            })
        },
    })
}

export const useBulkCreateOffers = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.offers.bulkCreate.useMutation({
        onSuccess: (data) => {
            utils.offers.list.invalidate()
            utils.offers.search.invalidate()
            toast({
                title: 'Success',
                description: data.message || 'Offers created successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to bulk create offers',
                variant: 'destructive',
            })
        },
    })
}

